import { asAmount, Amount, GSpread, asGSpread } from "@goodfill/domain-properties";
import { AccordionSection, Flex, InputNumber, InputRange, Span } from "@goodfill/ui-components";
import Decimal from "decimal.js";
import { useState } from "react";
import {
  getSessionStorageDecimal,
  getSessionStorageNumber,
  removeItemFromSessionStorage,
  saveDecimalToSessionStorage,
  saveNumberToSessionStorage,
} from "../../../../../sessionStorage";
import { PricedBond } from "../../../PricedBond";
import { BondFilter, BondFilterChipData, BondFilterData, BondFilterProps } from "../BondFilter";
import { BondFilterSectionGap } from "../BondFilterConst";
import { BondFilterCriteria } from "../BondFilterCriteria";
import { BondFilterDataConstraints } from "../BondFilterDataConstraints";

const FILTER_AMOUNT_OUTSTANDING_STORAGE_KEY = "filters:amountOutstanding";
const FILTER_ESG_STORAGE_KEY = "filters:esgScore";
const FILTER_G_SPREAD_ASK_STORAGE_KEY = "filters:gSpreadAsk";

const BILLION = 1_000_000_000;

// rome-ignore lint/suspicious/noExplicitAny:
type _Any = any;

const _Component = (props: BondFilterProps) => {
  /* Amount outstanding */
  const amountOutstanding = props.criteria.amountOutstanding;
  const amountOutstandingMin = props.constraints.amountOutstandingMin;
  const amountOutstandingMax = props.constraints.amountOutstandingMax;
  const amountOutstandingStep = asAmount(new Decimal(BILLION).div(10));
  const setAmountOutstanding = (value: Amount) => {
    props.onChange({
      criteria: { ...props.criteria, amountOutstanding: value },
    });
  };

  /* ESG score */
  const esgScore = props.criteria.esgScore;
  const esgScoreMin = props.constraints.esgScoreMin;
  const esgScoreMax = props.constraints.esgScoreMax;
  const esgScoreStep = 1;
  const setEsgScore = (value: number) => {
    props.onChange({
      criteria: { ...props.criteria, esgScore: value },
    });
  };

  /* G-Spread Ask*/
  const gSpreadAsk = Math.floor(props.criteria.gSpreadAsk.toNumber());
  const gSpreadAskMin = props.constraints.gSpreadAskMin;
  const gSpreadAskMax = props.constraints.gSpreadAskMax;
  const setGSpreadAsk = (value: GSpread) => {
    props.onChange({
      criteria: { ...props.criteria, gSpreadAsk: value },
    });
  };

  const [advancedOptionsSectionIsOpen, setAdvancedOptionsSectionIsOpen] = useState(false);

  const formatOutStandingAmount = (value: _Any) => `${value} bn`;

  return (
    <AccordionSection
      title={"Advanced options"}
      isOpen={advancedOptionsSectionIsOpen}
      setIsOpen={setAdvancedOptionsSectionIsOpen}
    >
      <Flex direction="col">
        <Flex direction="col" gap={BondFilterSectionGap}>
          <Span primary={true} uppercase={true}>
            Offer G-Spread (Basis Points=0.01%)
          </Span>
          <InputRange
            leftBound={gSpreadAsk}
            setLeftBound={(value) => setGSpreadAsk(asGSpread(new Decimal(value)))}
            min={gSpreadAskMin.toNumber()}
            max={gSpreadAskMax.toNumber()}
          />
          <Flex direction="col" items="center">
            <InputNumber
              border={"neutral"}
              width={"w-20"}
              min={gSpreadAskMin.toNumber()}
              max={gSpreadAskMax.toNumber()}
              value={gSpreadAsk}
              setValue={(value) => {
                if (value === null) return;
                setGSpreadAsk(asGSpread(new Decimal(value)));
              }}
              transparent={true}
              textAlign="center"
            />
          </Flex>
        </Flex>

        <Flex direction="col" gap={BondFilterSectionGap}>
          <Span primary={true} uppercase={true}>
            Amount Outstanding
          </Span>

          <InputRange
            leftBound={amountOutstanding.toNumber()}
            setLeftBound={(value) => setAmountOutstanding(asAmount(new Decimal(value)))}
            step={amountOutstandingStep.toNumber()}
            min={amountOutstandingMin.toNumber()}
            max={amountOutstandingMax.toNumber()}
          />
          <Flex direction="col" items="center">
            <InputNumber
              border={"neutral"}
              width={"w-20"}
              min={Number(amountOutstandingMin.div(BILLION).floor())}
              max={Number(amountOutstandingMax.div(BILLION).ceil())}
              step={Number(amountOutstandingStep.div(BILLION).floor())}
              value={Number(amountOutstanding.div(BILLION).toFixed(1))}
              setValue={(value) => {
                if (value === null) return;
                setAmountOutstanding(asAmount(new Decimal(value).floor().mul(BILLION)));
              }}
              formatValue={formatOutStandingAmount}
              transparent={true}
              textAlign="center"
            />
          </Flex>
        </Flex>

        <Flex direction="col" gap={BondFilterSectionGap}>
          <Span primary={true} uppercase={true}>
            ESG score
          </Span>
          <InputRange rightBound={esgScore} setRightBound={setEsgScore} min={esgScoreMin} max={esgScoreMax} />
          <Flex direction="col" items="center">
            <InputNumber
              border={"neutral"}
              width={"w-20"}
              min={esgScoreMin}
              max={esgScoreMax}
              step={esgScoreStep}
              value={esgScore}
              setValue={(value) => {
                if (value === null) return;
                setEsgScore(value);
              }}
              transparent={true}
              textAlign="center"
            />
          </Flex>
        </Flex>
      </Flex>
    </AccordionSection>
  );
};

export class BondFilterAdvanced implements BondFilter {
  isVisibleInDropdown(): boolean {
    return true;
  }

  evaluate(data: BondFilterData, criteria: BondFilterCriteria): boolean {
    return (
      data.pricedBond.askGSpread.greaterThanOrEqualTo(criteria.gSpreadAsk) &&
      data.pricedBond.outstandingAmount.amount.greaterThanOrEqualTo(criteria.amountOutstanding) &&
      Number(data.pricedBond.esgScore) <= criteria.esgScore
    );
  }

  getDefaultCriteriaConstraints(pricedBond: PricedBond): Partial<BondFilterDataConstraints> {
    return {
      amountOutstandingMin: pricedBond.outstandingAmount.amount,
      amountOutstandingMax: pricedBond.outstandingAmount.amount,
      esgScoreMin: Number(pricedBond.esgScore),
      esgScoreMax: Number(pricedBond.esgScore),
      gSpreadAskMin: pricedBond.askGSpread,
      gSpreadAskMax: pricedBond.askGSpread,
    };
  }

  updateConstraintByPricedBond(pricedBond: PricedBond, current: BondFilterDataConstraints): BondFilterDataConstraints {
    const result = { ...current };

    if (pricedBond.outstandingAmount.amount.greaterThan(result.amountOutstandingMax)) {
      result.amountOutstandingMax = pricedBond.outstandingAmount.amount;
    }
    if (pricedBond.outstandingAmount.amount.lessThan(result.amountOutstandingMin)) {
      result.amountOutstandingMin = pricedBond.outstandingAmount.amount;
    }

    if (pricedBond.askGSpread.greaterThan(result.gSpreadAskMax)) {
      result.gSpreadAskMax = pricedBond.askGSpread;
    }
    if (pricedBond.askGSpread.lessThan(result.gSpreadAskMin)) {
      result.gSpreadAskMin = pricedBond.askGSpread;
    }

    const esgScore = Number(pricedBond.esgScore);

    if (esgScore > result.esgScoreMax) {
      result.esgScoreMax = esgScore;
    }
    if (esgScore < result.esgScoreMin) {
      result.esgScoreMin = esgScore;
    }

    return result;
  }

  afterCriteriaCalculation(current: BondFilterDataConstraints): BondFilterDataConstraints {
    return current;
  }

  reset(criteria: BondFilterCriteria, defaultCriteria: BondFilterCriteria): BondFilterCriteria {
    return {
      ...criteria,
      amountOutstanding: defaultCriteria.amountOutstanding,
      esgScore: defaultCriteria.esgScore,
      gSpreadAsk: defaultCriteria.gSpreadAsk,
    };
  }

  getChips(criteria: BondFilterCriteria, defaultCriteria: BondFilterCriteria): BondFilterChipData[] {
    const chips: BondFilterChipData[] = [];

    if (!criteria.amountOutstanding.equals(defaultCriteria.amountOutstanding)) {
      chips.push({
        id: "amountOutstanding",
        label: "Amount Outstanding",
        onClick: () => ({ ...criteria, amountOutstanding: defaultCriteria.amountOutstanding }),
      });
    }

    if (criteria.esgScore !== defaultCriteria.esgScore) {
      chips.push({
        id: "esgScore",
        label: "ESG Score",
        onClick: () => ({ ...criteria, esgScore: defaultCriteria.esgScore }),
      });
    }

    if (!criteria.gSpreadAsk.equals(defaultCriteria.gSpreadAsk)) {
      chips.push({
        id: "gSpreadAsk",
        label: "Ask G-Spread",
        onClick: () => ({ ...criteria, gSpreadAsk: defaultCriteria.gSpreadAsk }),
      });
    }

    return chips;
  }

  component = _Component;

  applyCriteriaFromStorage(criteria: Partial<BondFilterCriteria>): Partial<BondFilterCriteria> {
    const rawOutstandingAmount = getSessionStorageDecimal(FILTER_AMOUNT_OUTSTANDING_STORAGE_KEY);
    const rawGSpreadAsk = getSessionStorageDecimal(FILTER_G_SPREAD_ASK_STORAGE_KEY);

    return {
      ...criteria,
      amountOutstanding: rawOutstandingAmount ? asAmount(rawOutstandingAmount) : undefined,
      esgScore: getSessionStorageNumber(FILTER_ESG_STORAGE_KEY),
      gSpreadAsk: rawGSpreadAsk ? asGSpread(rawGSpreadAsk) : undefined,
    };
  }

  storeCriteriaToStorage(criteria: BondFilterCriteria, defaultCriteria: BondFilterCriteria): void {
    if (criteria.amountOutstanding.equals(defaultCriteria.amountOutstanding)) {
      removeItemFromSessionStorage(FILTER_AMOUNT_OUTSTANDING_STORAGE_KEY);
    } else {
      saveDecimalToSessionStorage(FILTER_AMOUNT_OUTSTANDING_STORAGE_KEY, criteria.amountOutstanding, 0);
    }

    if (criteria.esgScore === defaultCriteria.esgScore) {
      removeItemFromSessionStorage(FILTER_ESG_STORAGE_KEY);
    } else {
      saveNumberToSessionStorage(FILTER_ESG_STORAGE_KEY, criteria.esgScore);
    }

    if (criteria.gSpreadAsk.equals(defaultCriteria.gSpreadAsk)) {
      removeItemFromSessionStorage(FILTER_G_SPREAD_ASK_STORAGE_KEY);
    } else {
      saveDecimalToSessionStorage(FILTER_G_SPREAD_ASK_STORAGE_KEY, criteria.gSpreadAsk);
    }
  }
}
